import React, { FormEvent, useState } from "react";
import { DashboardRole, session } from "./session";

interface LoginFormProps {
  onSuccess: () => void;
}

export function LoginForm({ onSuccess }: LoginFormProps): React.JSX.Element {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await session.loginWithCredentials(email, password);
      onSuccess();
    } catch {
      setError("Unable to authenticate. Please check your credentials.");
    } finally {
      setBusy(false);
    }
  }

  function handlePersonaSelect(role: DashboardRole) {
    session.selectPersona(role);
    onSuccess();
  }

  return (
    <div className="login-page-container">
      <div className="login-card">
        <div className="login-header">
          <div className="brand-logo-large" aria-hidden="true">
            ♞
          </div>
          <h1>Sign in to Zhatura</h1>
          <p className="login-subtitle">
            Unified Web Portal for Students, Parents, Coaches & Academies
          </p>
        </div>

        {error && (
          <div className="alert-box alert-error" role="alert">
            {error}
          </div>
        )}

        <form
          onSubmit={(e) => {
            void handleSubmit(e);
          }}
          className="login-form"
        >
          <div className="form-group">
            <label htmlFor="login-email">Email Address</label>
            <input
              id="login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@example.com"
              disabled={busy}
            />
          </div>

          <div className="form-group">
            <label htmlFor="login-password">Password</label>
            <input
              id="login-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              disabled={busy}
            />
          </div>

          <button
            type="submit"
            className="btn-primary btn-full"
            disabled={busy}
          >
            {busy ? "Signing in…" : "Sign In"}
          </button>
        </form>

        <div className="login-divider">
          <span>Or explore with verified personas</span>
        </div>

        <div className="persona-quick-grid">
          <button
            type="button"
            className="persona-btn"
            onClick={() => handlePersonaSelect("student")}
          >
            <span className="persona-role">Student / Player</span>
            <span className="persona-name">Arjun K. (Rapid 1540)</span>
          </button>

          <button
            type="button"
            className="persona-btn"
            onClick={() => handlePersonaSelect("parent")}
          >
            <span className="persona-role">Parent / Guardian</span>
            <span className="persona-name">Priya K. (2 Wards)</span>
          </button>

          <button
            type="button"
            className="persona-btn"
            onClick={() => handlePersonaSelect("coach")}
          >
            <span className="persona-role">Coach</span>
            <span className="persona-name">Coach Ramesh S. (12 Students)</span>
          </button>

          <button
            type="button"
            className="persona-btn"
            onClick={() => handlePersonaSelect("academy")}
          >
            <span className="persona-role">Academy / Org</span>
            <span className="persona-name">TN Chess Academy (48 Roster)</span>
          </button>
        </div>

        <div className="login-footer-links">
          <p className="admin-link-note">
            Platform workforce operator?{" "}
            <a
              href="https://zhatura-admin-staging-iz6k7kv5ra-lz.a.run.app"
              target="_blank"
              rel="noopener noreferrer"
            >
              Access Admin Control Center (MFA)
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
